import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { EnvironmentId, ThreadId } from "@t3tools/contracts";
import { loadOlderThreadMessages } from "../../environments/runtime/service";
import type { MessageWindowState } from "../../store";
import { nextMessageLinkAction } from "./useMessageLink.logic";
import type { TimelineScrollHandle } from "./useTimelineScroll";

export function useRailMessageSelection(input: {
  environmentId: EnvironmentId;
  threadId: ThreadId;
  messages: ReadonlyArray<{ id: string }> | undefined;
  window: MessageWindowState | undefined;
  timelineRef: RefObject<TimelineScrollHandle | null>;
}) {
  const { environmentId, threadId, messages, window, timelineRef } = input;
  const scope = JSON.stringify([environmentId, threadId]);
  const [selection, setSelection] = useState<{
    scope: string;
    messageId: string;
    request: number;
  } | null>(null);
  const progressRef = useRef({ key: "", completed: false, cursor: "" });
  const selectMessage = useCallback(
    (messageId: string) => {
      setSelection((previous) => ({ scope, messageId, request: (previous?.request ?? 0) + 1 }));
    },
    [scope],
  );
  useEffect(() => {
    if (selection?.scope !== scope) return;
    const key = JSON.stringify([scope, selection.messageId, selection.request]);
    if (progressRef.current.key !== key)
      progressRef.current = { key, completed: false, cursor: "" };
    const progress = progressRef.current;
    const action = nextMessageLinkAction(progress, selection.messageId, messages, window);
    if (action === "scroll") {
      const frame = requestAnimationFrame(() => {
        if (!timelineRef.current) return;
        timelineRef.current.scrollToMessage(selection.messageId);
        progress.completed = true;
      });
      return () => cancelAnimationFrame(frame);
    }
    if (action === "load") loadOlderThreadMessages(environmentId, threadId);
    return undefined;
  }, [environmentId, threadId, scope, selection, messages, window, timelineRef]);
  return selectMessage;
}
